// FLAGS: health-data
// Files attached to one consult's session notes: drag-and-drop upload, delete, and links that open the
// stored file in a new tab.
import { FC, useCallback, useState } from "react";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { getApiBaseUrl } from "@src/utils/urlHelpers";
import { useDropzone, FileRejection, ErrorCode } from "react-dropzone";
import {
  useDeleteSessionNoteAttachmentMutation,
  useUploadSessionNoteAttachmentMutation,
  useSessionNoteAttachmentsQuery,
} from "@src/queries/consults";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

const UploadErrors: FC<{ errors: string[] }> = ({ errors }) => {
  const { t } = useTranslation();
  const errorDescriptions: Record<ErrorCode, string> = {
    [ErrorCode.FileInvalidType]: t(
      "AttachmentsBox.UploadErrors.FileInvalidType",
    ),
    [ErrorCode.FileTooLarge]: t("AttachmentsBox.UploadErrors.FileTooLarge"),
    [ErrorCode.TooManyFiles]: t("AttachmentsBox.UploadErrors.FileTooMany"),
    [ErrorCode.FileTooSmall]: "",
  };
  return errors.map((error) => (
    <p
      key={error}
      className="text-red-500 border border-solid border-red-500 rounded-lg p-2 my-2 sub2"
    >
      <ErrorOutline className="w-5 h-5 mr-1 align-middle" />
      {errorDescriptions[error as ErrorCode] ?? error}
    </p>
  ));
};
interface AttachmentsBoxProps {
  consultId: number;
  className?: string;
}

const AttachmentsBox: FC<AttachmentsBoxProps> = ({
  consultId,
  className = "",
}) => {
  const { t } = useTranslation();
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [deletingFileName, setDeletingFileName] = useState("");
  const maxAttachmentCount = 3;
  const { data: attachments } = useSessionNoteAttachmentsQuery(consultId);
  const { mutateAsync: uploadSessionNoteAttachment, isPending: isUploading } =
    useUploadSessionNoteAttachmentMutation(consultId);
  const {
    mutateAsync: deleteSessionNoteAttachment,
    isPending: isDeletingAttachment,
  } = useDeleteSessionNoteAttachmentMutation(consultId);

  const appInsights = useAppInsightsContext();
  const handleFileUploadError = (err: any) => {
    appInsights.trackException(err);
    setUploadErrors((prevState) => [
      ...prevState,
      t("AttachmentsBox.UploadErrors.OperationFailed"),
    ]);
  };

  // FLAGS: health-data
  // Whatever the operator drops becomes part of a client's clinical record; the accept list and the
  // 25 MB cap here are the only content limits applied before upload.
  const onFilesSelected = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setUploadErrors(
        fileRejections.flatMap((rejection) =>
          rejection.errors.map((error) => error.code),
        ),
      );
      acceptedFiles.forEach((file) => {
        const formData = new FormData();
        formData.append("file", file, file?.name);

        uploadSessionNoteAttachment(formData).catch(handleFileUploadError);
      });
    },
    [],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onFilesSelected,
    maxFiles: maxAttachmentCount - (attachments?.length ?? 0),
    multiple: true,
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "image/gif": [".gif"],
      "image/webp": [".webp"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-excel": [".xls", ".xlsx"],
      "audio/*": [],
    },
    maxSize: 25 * 1024 * 1024,
  });

  return (
    <div className={`p-3 ${className}`}>
      <p className="sub1 py-2">
        {t("AttachmentsBox.Title")}
        <span className="ml-2 text-gray-400 sub2">
          {attachments?.length ?? 0}/{maxAttachmentCount} files
        </span>
      </p>
      <p className="p-4 border border-solid border-gray-300 rounded-lg sub2 text-gray-800">
        <WarningAmberRounded className="w-6 h-6 mr-2 align-bottom" />
        {t("AttachmentsBox.Warning")}
      </p>
      {(attachments?.length ?? 0) < maxAttachmentCount && (
        <div
          {...getRootProps()}
          className="p-2 mt-2 flex flex-col items-center justify-center border border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer transition hover:bg-gray-50 relative"
        >
          <input {...getInputProps()} />
          <FileUploadOutlined className="fill-gray-400 w-10 h-10 mb-1" />
          <p className="sub1">{t("AttachmentsBox.DropFilesHere.Title")}</p>
          <p className="cap2 text-gray-400 ">
            {t("AttachmentsBox.DropFilesHere.Subtitle")}
          </p>
          {isUploading && (
            <div className="absolute inset-0 m-auto flex items-center justify-center">
              <Spinner />
            </div>
          )}
        </div>
      )}
      <UploadErrors errors={uploadErrors} />
      <div className="flex flex-wrap gap-2 mt-4 transition">
        {attachments?.map((attachment) => (
          <div
            key={attachment.fileName}
            className="flex items-center gap-2 px-2 rounded-lg border-solid border border-gray-300"
          >
            <AttachFileOutlined className="w-4 h-4 align-bottom" />
            <a
              href={new URL(getApiBaseUrl()).origin + attachment.url}
              target="_blank"
              rel="noreferrer"
              className="no-underline hover:underline decoration-gray-400"
            >
              <span className="cap1">{attachment.fileName}</span>
            </a>
            <button
              type="button"
              aria-label="Delete attachment"
              className="text-red-500 bg-white border-0 border-solid border-l border-gray-300 cursor-pointer"
              onClick={() => {
                setDeletingFileName(attachment.fileName);
                deleteSessionNoteAttachment(attachment.fileName);
              }}
            >
              {isDeletingAttachment &&
              deletingFileName === attachment.fileName ? (
                <CircularProgress size="16px" />
              ) : (
                <DeleteOutline className="w-5 h-5 align-bottom" />
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttachmentsBox;
